/**
 * IndexedDB implementation of IReadingPositionStorage
 * Uses browser's IndexedDB API for persistence
 */

import type { IReadingPosition, IReadingPositionStorage } from '../../../contracts/storage.ts';
import { IndexedDBWrapper } from './indexeddb-wrapper.ts';

const STORE_NAME = 'readingPositions';
const DEBOUNCE_DELAY = 1000; // 1 second debounce

export class IndexedDBReadingPositionStorage implements IReadingPositionStorage {
  private db: IndexedDBWrapper;
  private debounceTimers: Map<string, NodeJS.Timeout> = new Map();

  constructor(db?: IndexedDBWrapper) {
    this.db = db || new IndexedDBWrapper();
  }

  async savePosition(position: IReadingPosition): Promise<void> {
    const { bookId } = position;

    // Clear existing timer for this book
    const existingTimer = this.debounceTimers.get(bookId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set up new debounced save
    return new Promise((resolve) => {
      const timer = setTimeout(async () => {
        await this.savePositionImmediate(position);
        this.debounceTimers.delete(bookId);
        resolve();
      }, DEBOUNCE_DELAY);

      this.debounceTimers.set(bookId, timer);
    });
  }

  private async savePositionImmediate(position: IReadingPosition): Promise<void> {
    await this.db.put(STORE_NAME, position);
  }

  async getPosition(bookId: string): Promise<IReadingPosition | null> {
    return await this.db.get<IReadingPosition>(STORE_NAME, bookId);
  }

  async getAllPositions(): Promise<IReadingPosition[]> {
    return await this.db.getAll<IReadingPosition>(STORE_NAME);
  }

  async deletePosition(bookId: string): Promise<void> {
    // Clear any pending save
    const timer = this.debounceTimers.get(bookId);
    if (timer) {
      clearTimeout(timer);
      this.debounceTimers.delete(bookId);
    }

    await this.db.delete(STORE_NAME, bookId);
  }
}
